import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { FileText, Download, RefreshCw, HandCoins, Plus, CheckCircle2, Trash2, Clock, Square, CheckSquare } from "lucide-react";
import Layout from "../components/Layout";
import { formatCurrency, formatDate } from "../lib/propiedadUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const ESTATUS_ENTREGA = {
  entregado: { label: "Efectivo entregado", className: "bg-emerald-100 text-emerald-700" },
  parcial: { label: "Entrega parcial", className: "bg-amber-100 text-amber-700" },
  pendiente: { label: "Pendiente de entrega", className: "bg-red-100 text-red-700" },
  no_aplica: { label: "Sin efectivo por entregar", className: "bg-gray-100 text-gray-600" },
};

const METODO_LABEL = { efectivo: "Efectivo", transferencia: "Transferencia", cheque: "Cheque", otro: "Otro" };

export default function Caratulas({ user, onLogout }) {
  const now = new Date();
  const [propietarios, setPropietarios] = useState([]);
  const [propietario, setPropietario] = useState("");
  const [mes, setMes] = useState(String(now.getMonth() + 1));
  const [anio, setAnio] = useState(String(now.getFullYear()));
  const [caratula, setCaratula] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seleccion, setSeleccion] = useState(null);
  const [showEntrega, setShowEntrega] = useState(false);
  const [entregaForm, setEntregaForm] = useState({});
  const [savingEntrega, setSavingEntrega] = useState(false);
  const esPropietarioRol = user.rol === "propietario";

  useEffect(() => {
    axios.get(`${API}/reportes/propietarios-lista`, { headers: authHeader() })
      .then((r) => {
        setPropietarios(r.data);
        if (user.rol === "propietario" && r.data.length === 1) setPropietario(r.data[0].email);
      })
      .catch(() => toast.error("Error al cargar propietarios"));
  }, []);

  const periodo = `${anio}-${String(mes).padStart(2, "0")}`;

  useEffect(() => {
    setCaratula(null);
    setSeleccion(null);
  }, [propietario, mes, anio]);

  const generar = async (selIds = seleccion) => {
    if (!propietario) return toast.error("Selecciona un propietario");
    setLoading(true);
    try {
      const params = { propietario_email: propietario, periodo };
      if (selIds?.length) params.property_ids = selIds.join(",");
      const r = await axios.get(`${API}/caratula`, { params, headers: authHeader() });
      setCaratula(r.data);
      if (!selIds) setSeleccion(r.data.propiedades_todas.map((p) => p.property_id));
    } catch (e) {
      setCaratula(null);
      toast.error(e.response?.data?.detail || "Error al generar la carátula");
    } finally {
      setLoading(false);
    }
  };

  const descargarPDF = async () => {
    try {
      const params = { propietario_email: propietario, periodo };
      if (seleccion?.length) params.property_ids = seleccion.join(",");
      const r = await axios.get(`${API}/caratula-pdf`, {
        params,
        headers: authHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `caratula_${periodo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar el PDF");
    }
  };

  const abrirEntrega = () => {
    setEntregaForm({
      fecha_entrega: new Date().toISOString().split("T")[0],
      monto: String(caratula?.entrega?.saldo_por_entregar ?? ""),
      metodo: "efectivo",
      referencia: "",
      observaciones: "",
    });
    setShowEntrega(true);
  };

  const guardarEntrega = async () => {
    const monto = parseFloat(entregaForm.monto);
    if (!monto || monto <= 0) return toast.error("Captura un monto válido");
    setSavingEntrega(true);
    try {
      await axios.post(`${API}/entregas`, {
        propietario_email: propietario,
        periodo,
        fecha_entrega: entregaForm.fecha_entrega,
        monto,
        property_ids: seleccion || [],
        metodo: entregaForm.metodo,
        referencia: entregaForm.referencia || null,
        observaciones: entregaForm.observaciones || null,
      }, { headers: authHeader() });
      toast.success("Entrega registrada");
      setShowEntrega(false);
      await generar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar la entrega");
    } finally {
      setSavingEntrega(false);
    }
  };

  const eliminarEntrega = async (id) => {
    try {
      await axios.delete(`${API}/entregas/${id}`, { headers: authHeader() });
      toast.success("Entrega eliminada");
      await generar();
    } catch {
      toast.error("Error al eliminar la entrega");
    }
  };

  const toggleProp = (pid) => {
    const nueva = seleccion.includes(pid) ? seleccion.filter((x) => x !== pid) : [...seleccion, pid];
    if (!nueva.length) return toast.error("Selecciona al menos una propiedad");
    setSeleccion(nueva);
    generar(nueva);
  };

  const marcarPago = async (p) => {
    try {
      await axios.put(`${API}/caratula/pago-propiedad`, {
        propietario_email: propietario,
        periodo,
        property_id: p.property_id,
        pagada: !p.pagada,
      }, { headers: authHeader() });
      toast.success(!p.pagada ? "Propiedad marcada como pagada" : "Propiedad marcada como pendiente");
      await generar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar el estatus de pago");
    }
  };

  const r = caratula?.resumen;
  const ent = caratula?.entrega;
  const estatusCfg = ent ? ESTATUS_ENTREGA[ent.estatus] : null;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="caratulas-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Carátulas mensuales</h1>
          <p className="text-sm text-gray-500 mt-1">Estado de cuenta consolidado por propietario y periodo</p>
        </div>

        {/* Selección */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <Select value={propietario} onValueChange={setPropietario}>
                <SelectTrigger data-testid="caratula-propietario-select"><SelectValue placeholder="Propietario" /></SelectTrigger>
                <SelectContent>
                  {propietarios.map((p) => <SelectItem key={p.email} value={p.email}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={mes} onValueChange={setMes}>
                <SelectTrigger data-testid="caratula-mes-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={anio} onValueChange={setAnio}>
                <SelectTrigger data-testid="caratula-anio-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => now.getFullYear() - 3 + i).map((a) => (
                    <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => generar()} disabled={loading} data-testid="generar-caratula-btn">
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> {caratula ? "Regenerar" : "Generar carátula"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {!caratula && !loading && (
          <Card>
            <CardContent className="py-16 text-center text-gray-500">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              Selecciona propietario y periodo para generar la carátula
            </CardContent>
          </Card>
        )}

        {caratula && (
          <div className="space-y-5" data-testid="caratula-preview">
            {/* Encabezado */}
            <Card>
              <CardContent className="pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{caratula.propietario.nombre}</h2>
                  <p className="text-sm text-gray-500">Periodo: {caratula.periodo_label} · Generada: {formatDate(caratula.fecha_generacion)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {estatusCfg && (
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${estatusCfg.className}`} data-testid="estatus-entrega-badge">
                      {estatusCfg.label}
                    </span>
                  )}
                  <Button variant="outline" onClick={descargarPDF} data-testid="descargar-pdf-btn">
                    <Download className="w-4 h-4 mr-1" /> Descargar PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Selector de propiedades incluidas */}
            {!esPropietarioRol && caratula.propiedades_todas?.length > 1 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Propiedades incluidas en esta carátula</CardTitle></CardHeader>
                <CardContent data-testid="selector-propiedades">
                  <div className="flex flex-wrap gap-2">
                    {caratula.propiedades_todas.map((p) => {
                      const activa = seleccion?.includes(p.property_id);
                      return (
                        <button
                          key={p.property_id}
                          onClick={() => toggleProp(p.property_id)}
                          data-testid={`prop-chip-${p.property_id}`}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                            activa ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-400 border-gray-300 hover:border-gray-400"
                          }`}
                        >
                          {activa ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                          {p.nombre}
                          <span className={`w-2 h-2 rounded-full ${p.pagada ? "bg-emerald-400" : "bg-amber-400"}`} title={p.pagada ? "Pagada" : "Pendiente de pago"} />
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Marca solo las propiedades que vas a liquidar en esta visita. Punto verde = ya pagada al propietario este periodo, ámbar = pendiente.</p>
                </CardContent>
              </Card>
            )}

            {/* Desglose por propiedad */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Desglose por propiedad</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="caratula-desglose">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                      <th className="px-4 py-2">Propiedad</th>
                      <th className="px-4 py-2">Inquilino</th>
                      <th className="px-4 py-2 text-right">Renta del mes</th>
                      <th className="px-4 py-2 text-right">Cobrado</th>
                      <th className="px-4 py-2 text-right">Efectivo</th>
                      <th className="px-4 py-2 text-right">Depósito</th>
                      <th className="px-4 py-2 text-right">Gastos</th>
                      <th className="px-4 py-2 text-right">Cuota cond.</th>
                      <th className="px-4 py-2 text-right">Honorarios</th>
                      <th className="px-4 py-2 text-right">Neto efectivo</th>
                      <th className="px-4 py-2 text-center">Pago al propietario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caratula.propiedades.map((p) => (
                      <tr key={p.property_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2.5 font-medium text-gray-900">{p.nombre}</td>
                        <td className="px-4 py-2.5 text-gray-600">{p.inquilino || "—"}</td>
                        <td className="px-4 py-2.5 text-right">{formatCurrency(p.renta_mensual)}</td>
                        <td className="px-4 py-2.5 text-right font-medium">{formatCurrency(p.renta_cobrada)}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-700">{formatCurrency(p.cobrado_efectivo)}</td>
                        <td className="px-4 py-2.5 text-right text-indigo-700">{formatCurrency(p.cobrado_deposito)}</td>
                        <td className="px-4 py-2.5 text-right text-red-600">{p.total_gastos ? `- ${formatCurrency(p.total_gastos)}` : "—"}</td>
                        <td className="px-4 py-2.5 text-right text-red-600">{p.cuota_descontada ? `- ${formatCurrency(p.cuota_descontada)}` : "—"}</td>
                        <td className="px-4 py-2.5 text-right text-red-600">
                          {p.honorarios ? `- ${formatCurrency(p.honorarios)}` : "—"}
                          {p.honorarios_estimados && <span className="block text-[10px] text-amber-500">estimado {p.honorarios_pct}%</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold">{formatCurrency(p.neto_efectivo)}</td>
                        <td className="px-4 py-2.5 text-center">
                          <button
                            onClick={() => !esPropietarioRol && marcarPago(p)}
                            disabled={esPropietarioRol}
                            data-testid={`toggle-pago-${p.property_id}`}
                            title={esPropietarioRol ? "" : "Click para cambiar el estatus"}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                              p.pagada ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                            } ${esPropietarioRol ? "cursor-default" : "cursor-pointer"}`}
                          >
                            {p.pagada ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                            {p.pagada ? "Pagada" : "Pendiente"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Resumen */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Resumen general</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm" data-testid="caratula-resumen">
                  <div className="flex justify-between"><span className="text-gray-500">Total de rentas cobradas</span><strong>{formatCurrency(r.total_rentas_cobradas)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Recibido en efectivo</span><strong className="text-emerald-700">{formatCurrency(r.total_efectivo)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Recibido por depósitos</span><strong className="text-indigo-700">{formatCurrency(r.total_depositos)}</strong></div>
                  <div className="flex justify-between pt-2 border-t border-gray-100"><span className="text-gray-500">Gastos</span><strong className="text-red-600">- {formatCurrency(r.total_gastos)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Cuotas condominales</span><strong className="text-red-600">- {formatCurrency(r.total_cuotas_condominales)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Honorarios de administración</span><strong className="text-red-600">- {formatCurrency(r.total_honorarios)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Total de deducciones</span><strong className="text-red-600">- {formatCurrency(r.total_deducciones)}</strong></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><HandCoins className="w-4 h-4 text-emerald-600" /> Entrega al propietario</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 flex justify-between items-center">
                    <span className="font-medium text-emerald-800">Efectivo a entregar</span>
                    <span className="text-xl font-bold text-emerald-700" data-testid="efectivo-a-entregar">{formatCurrency(r.efectivo_a_entregar)}</span>
                  </div>
                  <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3 flex justify-between items-center">
                    <span className="font-medium text-indigo-800">Depósitos ya recibidos</span>
                    <span className="text-lg font-bold text-indigo-700">{formatCurrency(r.depositos_recibidos)}</span>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 flex justify-between items-center">
                    <span className="font-medium text-gray-800">Total económico recibido</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(r.total_economico)}</span>
                  </div>
                  <p className="text-xs text-gray-500">Efectivo a entregar = rentas cobradas en efectivo − gastos − cuotas condominales − honorarios. Los depósitos ya fueron recibidos directamente por el propietario.</p>

                  {/* Entrega registrada */}
                  <div className="pt-3 border-t border-gray-100 space-y-2" data-testid="entregas-seccion">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-800 flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-gray-400" /> Entrega del efectivo
                      </p>
                      {!esPropietarioRol && ent?.estatus !== "no_aplica" && ent?.estatus !== "entregado" && (
                        <Button size="sm" onClick={abrirEntrega} data-testid="registrar-entrega-btn">
                          <Plus className="w-3.5 h-3.5 mr-1" /> Registrar entrega
                        </Button>
                      )}
                    </div>
                    {caratula.entregas?.length > 0 ? (
                      <div className="space-y-1.5">
                        {caratula.entregas.map((e) => (
                          <div key={e.id} className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
                            <span className="text-gray-700">
                              {formatDate(e.fecha_entrega)} · {METODO_LABEL[e.metodo] || e.metodo}
                              {e.referencia ? ` · Ref: ${e.referencia}` : ""}
                              <span className="block text-xs text-gray-400">Registró: {e.registrado_por_nombre}</span>
                            </span>
                            <span className="flex items-center gap-2">
                              <strong className="text-gray-900">{formatCurrency(e.monto)}</strong>
                              {!esPropietarioRol && (
                                <button onClick={() => eliminarEntrega(e.id)} className="text-gray-400 hover:text-red-500" data-testid={`eliminar-entrega-${e.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </span>
                          </div>
                        ))}
                        {ent?.estatus === "parcial" && (
                          <p className="text-xs text-amber-600">Por entregar: {formatCurrency(ent.saldo_por_entregar)}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">
                        {ent?.estatus === "no_aplica" ? "No hay efectivo por entregar en este periodo." : "Aún no se registra la entrega del efectivo."}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Modal registrar entrega */}
      <Dialog open={showEntrega} onOpenChange={setShowEntrega}>
        <DialogContent data-testid="registrar-entrega-modal">
          <DialogHeader>
            <DialogTitle>Registrar entrega de efectivo</DialogTitle>
            <DialogDescription>Registra cuándo y cómo se entregó el efectivo al propietario. Las propiedades incluidas en esta carátula se marcarán como pagadas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Fecha de entrega *</Label>
                <Input type="date" value={entregaForm.fecha_entrega || ""} onChange={(e) => setEntregaForm({ ...entregaForm, fecha_entrega: e.target.value })} data-testid="entrega-fecha-input" />
              </div>
              <div className="space-y-2">
                <Label>Monto entregado *</Label>
                <Input type="number" step="0.01" min="0" value={entregaForm.monto || ""} onChange={(e) => setEntregaForm({ ...entregaForm, monto: e.target.value })} data-testid="entrega-monto-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>¿Cómo se entregó?</Label>
              <Select value={entregaForm.metodo || "efectivo"} onValueChange={(v) => setEntregaForm({ ...entregaForm, metodo: v })}>
                <SelectTrigger data-testid="entrega-metodo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo en mano</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Referencia (opcional)</Label>
              <Input value={entregaForm.referencia || ""} onChange={(e) => setEntregaForm({ ...entregaForm, referencia: e.target.value })} placeholder="Folio, número de transferencia..." />
            </div>
            <div className="space-y-2">
              <Label>Observaciones (opcional)</Label>
              <Textarea rows={2} value={entregaForm.observaciones || ""} onChange={(e) => setEntregaForm({ ...entregaForm, observaciones: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntrega(false)}>Cancelar</Button>
            <Button onClick={guardarEntrega} disabled={savingEntrega} data-testid="entrega-guardar-btn">
              {savingEntrega ? "Guardando..." : "Registrar entrega"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
